import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileCheck2, CheckCircle2 } from "lucide-react";
import { importPreview, importCommit, downloadSampleCsv } from "../../riskprofiler/riskApi";

const statusPill = (s) => ({
    valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    duplicate: "bg-amber-50 text-[#b45309] border-amber-200",
    invalid: "bg-red-50 text-red-700 border-red-200",
}[s] || "");

export function RiskImportTab() {
    const fileRef = useRef(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setDone(null);
        try { setPreview(await importPreview(file)); }
        catch (err) { toast.error("Could not parse the CSV. Check the format."); }
        if (fileRef.current) fileRef.current.value = "";
    };

    const commit = async () => {
        setBusy(true);
        try {
            const r = await importCommit(preview.rows);
            setDone(r);
            toast.success(`Imported ${r.imported} record(s).`);
            setPreview(null);
        } catch { toast.error("Import failed."); }
        finally { setBusy(false); }
    };

    const c = preview?.counts;

    return (
        <div data-testid="rp-import-tab" className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm font-body text-oth-ink3 max-w-xl">Upload a CSV with columns <strong>name, email, age</strong> and one <strong>q1…qN</strong> column per active question (values a/b/c/d). Imported rows are scored automatically and do not trigger emails.</p>
                <button onClick={downloadSampleCsv} className="btn-pill btn-pill-light text-sm" data-testid="rp-download-sample"><Download size={14} /> Download Sample CSV</button>
            </div>

            <div className="rounded-2xl border border-dashed border-oth-line bg-white p-8 text-center">
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" id="rp-csv-input" data-testid="rp-csv-input" />
                <label htmlFor="rp-csv-input" className="btn-pill btn-pill-blue text-sm cursor-pointer inline-flex" data-testid="rp-upload-btn"><Upload size={14} /> Choose CSV file</label>
                <div className="text-xs text-oth-ink3 font-body mt-3">Invalid rows are highlighted and skipped. Duplicates (same name + email) are not imported.</div>
            </div>

            {done && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 flex items-center gap-3 text-sm font-body text-emerald-800" data-testid="rp-import-done">
                    <CheckCircle2 size={18} /> Imported <strong>{done.imported}</strong> record(s). Skipped <strong>{done.skipped}</strong> (invalid or duplicate).
                </div>
            )}

            {preview && (
                <div data-testid="rp-import-preview">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-body" data-testid="rp-count-valid">Valid: {c.valid}</span>
                        <span className="px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-700 text-sm font-body" data-testid="rp-count-invalid">Invalid: {c.invalid}</span>
                        <span className="px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 text-[#b45309] text-sm font-body" data-testid="rp-count-duplicate">Duplicate: {c.duplicate}</span>
                        <button onClick={commit} disabled={busy || c.valid === 0} className="btn-pill btn-pill-dark text-sm ml-auto disabled:opacity-50" data-testid="rp-import-commit"><FileCheck2 size={14} /> {busy ? "Importing…" : `Import ${c.valid} valid record(s)`}</button>
                    </div>
                    <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                        <div className="overflow-x-auto thin-scroll max-h-[480px] overflow-y-auto">
                            <table className="w-full text-sm font-body">
                                <thead className="sticky top-0"><tr className="bg-[#0A0A0A] text-white text-left text-[11px] uppercase tracking-[0.1em]">
                                    <th className="px-4 py-3">Line</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Age</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Issues</th>
                                </tr></thead>
                                <tbody>
                                    {preview.rows.map((r, i) => (
                                        <tr key={i} className={`border-b border-oth-line/40 ${r.status === "invalid" ? "bg-red-50/50" : r.status === "duplicate" ? "bg-amber-50/40" : ""}`} data-testid={`rp-preview-row-${i}`}>
                                            <td className="px-4 py-2.5 text-oth-ink3">{r.line}</td>
                                            <td className="px-4 py-2.5 text-oth-ink">{r.name}</td>
                                            <td className="px-4 py-2.5 text-oth-ink2">{r.email}</td>
                                            <td className="px-4 py-2.5">{r.age}</td>
                                            <td className="px-4 py-2.5"><span className={`inline-block px-2.5 py-0.5 rounded-full text-xs border ${statusPill(r.status)}`}>{r.status}</span></td>
                                            <td className="px-4 py-2.5 text-xs text-oth-ink3">{(r.errors || []).join(", ") || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
